'use client';

/**
 * FormChoiceField (storefront, live) — radio group (single value) or
 * checkbox group (array value), registered with react-hook-form. All inputs
 * share the field label as their name. Falls back to the shared preview
 * outside a FormContainer.
 */

import React from 'react';
import { FormChoiceField as Shared } from '@launchstore/shared-puck';
import type { FormChoiceFieldProps } from '@launchstore/shared-puck';
import { resolveColor } from '@launchstore/shared-puck';
import { useFormContext } from './FormContext';
import { buildValidationRules } from './form-live-helpers';

function FormChoiceFieldLive(props: FormChoiceFieldProps & { id?: string }) {
  const ctx = useFormContext();
  if (!ctx) return <>{(Shared.render as any)(props as FormChoiceFieldProps)}</>;

  const { register, errors } = ctx;
  const error = errors[props.label];
  const rules = buildValidationRules(props);

  return (
    <div className={`mt-${props.marginTop || '0'} ${props.fieldWidth === 'half' ? 'w-1/2' : 'w-full'}`}>
      <span
        className={`block text-${props.labelFontSize} font-${props.labelFontWeight} mb-1`}
        style={{ color: resolveColor(props.labelColor) }}
      >
        {props.label}
        {props.required && <span className="text-red-500 ml-0.5">*</span>}
      </span>

      <div className="flex flex-col gap-2" role={props.choiceType === 'radio' ? 'radiogroup' : 'group'}>
        {(props.options || []).map((opt, i) => (
          <label key={i} className="flex items-center gap-2 text-sm" style={{ color: resolveColor(props.inputTextColor) }}>
            <input
              type={props.choiceType}
              value={opt.label}
              style={{ accentColor: resolveColor(props.accentColor) }}
              {...register(props.label, rules)}
            />
            {opt.label}
          </label>
        ))}
      </div>

      {props.helpText && !error && (
        <p className="mt-1 text-xs text-gray-500">{props.helpText}</p>
      )}
      {error && (
        <p role="alert" className="mt-1 text-xs font-medium text-red-500">
          {String(error.message)}
        </p>
      )}
    </div>
  );
}

export const FormChoiceField: any = {
  ...Shared,
  render: (props: any) => <FormChoiceFieldLive {...props} />,
};

export default FormChoiceField;
